import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Linkedin, Mail, Menu, X, Phone, MessageCircle } from "lucide-react";

const SECTIONS = [
  { to: "/paid-organic-campaigns", label: "Campaigns" },
  { to: "/moment-marketing", label: "Moment Marketing" },
  { to: "/strategic-content-pillars", label: "Content IPs" },
  { to: "/broadcast-thought-leadership", label: "Broadcast" },
  { to: "/simplifying-finance-storytelling", label: "Storytelling" },
  { to: "/platform-native-strategies", label: "Platform Native" },
  { to: "/audience-segmentation-data-driven", label: "Segmentation" },
];

export const Layout = ({ children }) => {
  const { pathname } = useLocation();
  const [open, setOpen] = React.useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    setOpen(false);
  }, [pathname]);

  return (
    <div className="relative min-h-screen bg-[#0B0E14] text-[#EDE4D3]">
      <header className="sticky top-0 z-50 border-b border-[#EDE4D3]/10 bg-[#0B0E14]/85 backdrop-blur-md">
        <div className="mx-auto max-w-[1440px] px-6 py-4 lg:px-12">
          <div className="flex items-center gap-4">
            {/* Home block */}
            <Link
              to="/"
              data-testid="brand-link"
              className={`flex h-12 shrink-0 items-center justify-center rounded-sm border px-5 text-[11px] uppercase tracking-[0.24em] transition-colors ${
                pathname === "/"
                  ? "border-[#D4A574] text-[#D4A574]"
                  : "border-[#EDE4D3]/15 text-[#EDE4D3]/85 hover:border-[#D4A574]/60 hover:text-[#D4A574]"
              }`}
            >
              Home
            </Link>

            {/* Section blocks - equal width */}
            <nav className="hidden flex-1 xl:grid xl:grid-cols-7 xl:gap-2">
              {SECTIONS.map((item) => {
                const active = pathname === item.to;
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    data-testid={`nav-${item.to.replace(/\//g, "")}`}
                    className={`flex h-12 items-center justify-center rounded-sm border px-2 text-center text-[10px] uppercase leading-tight tracking-[0.18em] transition-colors ${
                      active
                        ? "border-[#D4A574] text-[#D4A574]"
                        : "border-[#EDE4D3]/12 text-[#EDE4D3]/75 hover:border-[#D4A574]/60 hover:text-[#D4A574]"
                    }`}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </nav>

            <button
              data-testid="menu-toggle"
              onClick={() => setOpen(!open)}
              className="ml-auto grid h-12 w-12 place-items-center rounded-sm border border-[#EDE4D3]/20 xl:hidden"
              aria-label="Toggle menu"
            >
              {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </button>
          </div>
        </div>

        {open && (
          <div className="border-t border-[#EDE4D3]/10 bg-[#0B0E14] xl:hidden">
            <nav className="mx-auto flex max-w-[1440px] flex-col px-6 py-6">
              {SECTIONS.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`py-3 text-sm uppercase tracking-[0.2em] ${
                    pathname === item.to ? "text-[#D4A574]" : "text-[#EDE4D3]/75"
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </nav>
          </div>
        )}
      </header>

      <main className="relative">{children}</main>

      <footer className="relative mt-32 overflow-hidden border-t border-[#EDE4D3]/10 bg-[#0B0E14]">
        {/* Accent ribbon */}
        <div className="overflow-hidden border-y border-[#EDE4D3]/10 py-5">
          <div className="marquee-track flex w-max gap-12 whitespace-nowrap font-serif text-2xl italic text-[#D4A574]/70 md:text-3xl">
            {Array.from({ length: 2 }).map((_, i) => (
              <React.Fragment key={i}>
                <span>Social Media Strategy</span>
                <span className="text-[#EDE4D3]/30">✦</span>
                <span>Content Architecture</span>
                <span className="text-[#EDE4D3]/30">✦</span>
                <span>Cultural Fluency</span>
                <span className="text-[#EDE4D3]/30">✦</span>
                <span>Broadcast Storytelling</span>
                <span className="text-[#EDE4D3]/30">✦</span>
                <span>Data-Driven Editorial</span>
                <span className="text-[#EDE4D3]/30">✦</span>
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="mx-auto max-w-[1440px] px-6 py-20 lg:px-12">
          <div className="grid gap-12 md:grid-cols-[1.4fr,1fr]">
            <div>
              <p className="font-serif text-3xl leading-tight md:text-4xl">
                Let&rsquo;s build something
                <br />
                <span className="italic text-[#D4A574]">worth following.</span>
              </p>
              <p className="mt-6 max-w-md text-sm leading-relaxed text-[#EDE4D3]/55">
                Currently shaping social, content, and broadcast strategy for finance audiences.
              </p>
            </div>

            <div>
              <p className="mb-4 text-[10px] uppercase tracking-[0.28em] text-[#EDE4D3]/40">
                Contact
              </p>
              <a
                href="mailto:tineshb@gmail.com"
                data-testid="footer-email"
                className="group mb-3 flex items-center gap-2 text-sm hover:text-[#D4A574]"
              >
                <Mail className="h-4 w-4" />
                tineshb@gmail.com
              </a>
              <a
                href="https://www.linkedin.com/in/tineshbhasin/"
                target="_blank"
                rel="noreferrer"
                data-testid="footer-linkedin"
                className="group mb-3 flex items-center gap-2 text-sm hover:text-[#D4A574]"
              >
                <Linkedin className="h-4 w-4" />
                linkedin.com/in/tineshbhasin
              </a>
              <a
                href="tel:+919833809757"
                data-testid="footer-phone"
                className="group mb-3 flex items-center gap-2 text-sm hover:text-[#D4A574]"
              >
                <Phone className="h-4 w-4" />
                +91 98338 09757
              </a>
              <a
                href="https://wa.me/919833809757"
                target="_blank"
                rel="noreferrer"
                data-testid="footer-whatsapp"
                className="group flex items-center gap-2 text-sm hover:text-[#D4A574]"
              >
                <MessageCircle className="h-4 w-4" />
                WhatsApp
              </a>
            </div>
          </div>

          <div className="mt-16 flex flex-col gap-3 border-t border-[#EDE4D3]/10 pt-6 text-xs text-[#EDE4D3]/40 md:flex-row md:items-center md:justify-between">
            <p>&copy; {new Date().getFullYear()} Tinesh Bhasin. All work referenced is from professional engagements.</p>
            <p className="italic">An editorial portfolio.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
